import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Paper,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add,
  Clear,
  Delete,
  Edit,
  Receipt,
  AttachMoney
} from '@mui/icons-material';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import AppTheme from '../../../../shared-theme/AppTheme';
import AppShellCobros from '../components/appshellcobros';
import { DataGrid } from '@mui/x-data-grid';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const GastosManagement = (props) => {
  const [gastos, setGastos] = useState([]);
  const [filteredGastos, setFilteredGastos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Alert dialog state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    message: '',
    title: '',
    severity: 'success'
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    fechaDesde: null,
    fechaHasta: null,
    montoMin: '',
    montoMax: ''
  });

  // Form states for new/edit gasto
  const [formData, setFormData] = useState({
    concepto: '',
    monto: '',
    descripcion: '',
    fecha: dayjs(),
    metodoPago: '',
    referencia: ''
  });

  // Static data - can be fetched from API
  const metodosPago = [
    'Efectivo',
    'Transferencia',
    'Cheque',
    'Tarjeta de Crédito',
    'Tarjeta de Débito'
  ];

  // Currency formatter
  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return 'RD$0.00';
    return `RD$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Alert helpers
  const showSuccessAlert = (title, message) => {
    setAlertConfig({
      title,
      message,
      severity: 'success'
    });
    setAlertOpen(true);
  };

  const showErrorAlert = (title, message) => {
    setAlertConfig({
      title,
      message,
      severity: 'error'
    });
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  // Calculate totals
  const totals = React.useMemo(() => {
    const totalGastos = filteredGastos.reduce((acc, g) => acc + (parseFloat(g.monto) || 0), 0);
    const totalTransactions = filteredGastos.length;
    
    // Get current month expenses
    const currentMonth = dayjs().month();
    const currentYear = dayjs().year();
    const monthlyExpenses = filteredGastos.filter(g => {
      const gastoDate = dayjs(g.fecha, 'DD/MM/YYYY');
      return gastoDate.month() === currentMonth && gastoDate.year() === currentYear;
    }).reduce((acc, g) => acc + (parseFloat(g.monto) || 0), 0);

    return {
      totalGastos,
      totalTransactions,
      monthlyExpenses
    };
  }, [filteredGastos]);

  // DataGrid columns
  const columns = [
    { 
      field: 'fecha', 
      headerName: 'Fecha', 
      flex: 1, 
      minWidth: 120 
    },
    { 
      field: 'concepto', 
      headerName: 'Concepto', 
      flex: 2, 
      minWidth: 250 
    },
    { 
      field: 'metodoPago', 
      headerName: 'Método de Pago', 
      flex: 1, 
      minWidth: 140,
      renderCell: (params) => {
        const colorMap = {
          'Efectivo': 'success',
          'Transferencia': 'info',
          'Cheque': 'warning',
          'Tarjeta de Crédito': 'secondary',
          'Tarjeta de Débito': 'default'
        };
        return (
          <Chip 
            label={params.row.metodoPago} 
            color={colorMap[params.row.metodoPago] || 'default'}
            size="small"
            sx={{ mt: 1 }}
          />
        );
      }
    },
    {
      field: 'monto',
      headerName: 'Monto',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="error.main" mt={2}>
          {formatNumberWithCommas(parseFloat(params.row.monto).toFixed(2))}
        </Typography>
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
          <Tooltip title="Editar">
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => handleEdit(params.row)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton 
              size="small" 
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Fetch gastos data
  const fetchGastos = async () => {
    setIsLoadingData(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/gastos?idcompany=${localStorage.getItem('idcompany')}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Gastos loaded from API:', data);
      setGastos(data);
      setFilteredGastos(data);
    } catch (error) {
      console.error('Error fetching gastos:', error);
      showErrorAlert('Error al cargar', `No se pudieron cargar los gastos: ${error.message}`);
      setGastos([]);
      setFilteredGastos([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Handle filter changes
  const applyFilters = () => {
    let filtered = [...gastos];

    if (filters.search) {
      filtered = filtered.filter(g => 
        g.concepto?.toLowerCase().includes(filters.search.toLowerCase()) ||
        g.descripcion?.toLowerCase().includes(filters.search.toLowerCase()) ||
        g.referencia?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.fechaDesde) {
      filtered = filtered.filter(g => {
        const gastoDate = dayjs(g.fecha, 'DD/MM/YYYY');
        return gastoDate.isAfter(filters.fechaDesde) || gastoDate.isSame(filters.fechaDesde, 'day');
      });
    }

    if (filters.fechaHasta) {
      filtered = filtered.filter(g => {
        const gastoDate = dayjs(g.fecha, 'DD/MM/YYYY');
        return gastoDate.isBefore(filters.fechaHasta) || gastoDate.isSame(filters.fechaHasta, 'day');
      });
    }

    if (filters.montoMin) {
      filtered = filtered.filter(g => parseFloat(g.monto) >= parseFloat(filters.montoMin));
    }

    if (filters.montoMax) {
      filtered = filtered.filter(g => parseFloat(g.monto) <= parseFloat(filters.montoMax));
    }

    setFilteredGastos(filtered);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      fechaDesde: null,
      fechaHasta: null,
      montoMin: '',
      montoMax: ''
    });
    setFilteredGastos(gastos);
  };

  // Handle dialog open/close
  const handleOpenDialog = () => {
    setEditMode(false);
    setSelectedGasto(null);
    setFormData({
      concepto: '',
      monto: '',
      descripcion: '',
      fecha: dayjs(),
      metodoPago: '',
      referencia: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedGasto(null);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.concepto || !formData.monto || !formData.metodoPago) {
      showErrorAlert('Campos requeridos', 'Por favor complete todos los campos requeridos: Concepto, Monto y Método de Pago');
      return;
    }

    try {
      const endpoint = editMode 
        ? `${process.env.REACT_APP_SERVER_URL}/api/gastos/${selectedGasto.id}`
        : `${process.env.REACT_APP_SERVER_URL}/api/gastos`;
      
      const method = editMode ? 'PUT' : 'POST';

      const payload = {
        concepto: formData.concepto,
        monto: parseFloat(formData.monto),
        descripcion: formData.descripcion || '',
        fecha: formData.fecha.format('DD/MM/YYYY'),
        metodoPago: formData.metodoPago,
        referencia: formData.referencia || '',
        idcompany: localStorage.getItem('idcompany'),
        id_sucursal: localStorage.getItem('id_sucursal')
      };

      console.log('Sending gasto data:', payload);

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Gasto saved successfully:', result);

      // Refresh data
      await fetchGastos();
      handleCloseDialog();
      
      showSuccessAlert(
        editMode ? '¡Actualizado!' : '¡Registrado!',
        editMode ? 'El gasto ha sido actualizado exitosamente' : 'El gasto ha sido registrado exitosamente'
      );
    } catch (error) {
      console.error('Error saving gasto:', error);
      showErrorAlert('Error al guardar', `No se pudo guardar el gasto: ${error.message}`);
    }
  };

  // Handle edit
  const handleEdit = (gasto) => {
    setEditMode(true);
    setSelectedGasto(gasto);
    setFormData({
      concepto: gasto.concepto || '',
      monto: gasto.monto || '',
      descripcion: gasto.descripcion || '',
      fecha: gasto.fecha ? dayjs(gasto.fecha, 'DD/MM/YYYY') : dayjs(),
      metodoPago: gasto.metodoPago || '',
      referencia: gasto.referencia || ''
    });
    setOpenDialog(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este gasto? Esta acción no se puede deshacer.')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/gastos/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Gasto deleted successfully:', result);

      await fetchGastos();
      showSuccessAlert('¡Eliminado!', 'El gasto ha sido eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting gasto:', error);
      showErrorAlert('Error al eliminar', `No se pudo eliminar el gasto: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchGastos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <AppShellCobros title="Bankivo · Centro de Cobros">
        <Box sx={{ p: 2, mr: 5 }}>
          {/* Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Gestión de Gastos
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Administra y controla todos los gastos operativos
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primarybutton"
                startIcon={<Add />}
                onClick={handleOpenDialog}
                sx={{ 
                  fontWeight: 600, 
                  px: 3,
                  py: 1.5
                }}
              >
                Registrar Gasto
              </Button>
            </Stack>
          </Stack>

          {/* Metric Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item size={4}>
              <Paper 
                sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #18586b 0%, #0f3a42 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(24, 88, 107, 0.3)',
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                      Total Gastos
                    </Typography>
                    <AttachMoney sx={{ fontSize: 28, opacity: 0.8 }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {currency(totals.totalGastos)}
                  </Typography>
                </Box>
                <Box sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  zIndex: 1,
                }} />
              </Paper>
            </Grid>
            <Grid item size={4}>
              <Paper 
                sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(239, 68, 68, 0.3)',
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                      Gastos del Mes
                    </Typography>
                    <Receipt sx={{ fontSize: 28, opacity: 0.8 }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {currency(totals.monthlyExpenses)}
                  </Typography>
                </Box>
                <Box sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  zIndex: 1,
                }} />
              </Paper>
            </Grid>
            <Grid item size={4}>
              <Paper 
                sx={{ 
                  p: 3, 
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(245, 158, 11, 0.3)',
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                      Total Transacciones
                    </Typography>
                    <Edit sx={{ fontSize: 28, opacity: 0.8 }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {totals.totalTransactions}
                  </Typography>
                </Box>
                <Box sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  zIndex: 1,
                }} />
              </Paper>
            </Grid>
          </Grid>

          {/* Filters Section */}
          <Paper sx={{ p: 2.5, mb: 3, backgroundColor: "white" }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item size={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 1, textAlign: "left", marginLeft: 1 }}>
                    Buscar
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Buscar por concepto, descripción o referencia..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>

              <Grid item size={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 1, textAlign: "left", marginLeft: 1 }}>
                    Fecha Desde
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={filters.fechaDesde}
                      onChange={(newValue) => setFilters({ ...filters, fechaDesde: newValue })}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>

              <Grid item size={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 1, textAlign: "left", marginLeft: 1 }}>
                    Fecha Hasta
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={filters.fechaHasta}
                      onChange={(newValue) => setFilters({ ...filters, fechaHasta: newValue })}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Grid>

              <Grid item size={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 1, textAlign: "left", marginLeft: 1 }}>
                    &nbsp;
                  </Typography>
                  <Button fullWidth variant="outlined" onClick={clearFilters}>
                    Limpiar Filtros
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Data Grid */}
          <Paper>
            {filteredGastos.length === 0 && !isLoadingData ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 8,
                textAlign: 'center'
              }}>
                <Receipt sx={{ fontSize: 80, color: '#55b4ac', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
                  No hay gastos registrados
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Comienza registrando tu primer gasto operativo
                </Typography>
                <Button
                  variant="contained"
                  color="primarybutton"
                  startIcon={<Add />}
                  onClick={handleOpenDialog}
                >
                  Registrar Primer Gasto
                </Button>
              </Box>
            ) : (
              <DataGrid
                autoHeight
                rows={filteredGastos}
                columns={columns}
                loading={isLoadingData}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
                  minHeight: 400
                }}
              />
            )}
          </Paper>
        </Box>
      </AppShellCobros>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight={700}>
            {editMode ? 'Editar Gasto' : 'Registrar Nuevo Gasto'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item size={6}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Concepto *
              </Typography>
              <TextField
                fullWidth
                value={formData.concepto}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                placeholder="Ej: Pago de electricidad"
                />
            </Grid>


            <Grid item size={6}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Monto *
              </Typography>
              <TextField
                fullWidth
                value={formatNumberWithCommas(formData.monto)}
                onChange={(e) => setFormData({ ...formData, monto: e.target.value.replace(/[^0-9.]/g, '') })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Fecha *
                </Typography>
                <DatePicker
                  disabled={true}
                  sx={{ backgroundColor: 'white' }}
                  value={formData.fecha}
                  onChange={(newValue) => setFormData({ ...formData, fecha: newValue })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item size={6}>
              <FormControl fullWidth required>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Método de Pago *
                </Typography>
                <Select
                  sx={{ backgroundColor: 'white' }}
                  value={formData.metodoPago}
                  onChange={(e) => setFormData({ ...formData, metodoPago: e.target.value })}
                  displayEmpty
                >
                  <MenuItem value="">Seleccionar método</MenuItem>
                  {metodosPago.map((metodo) => (
                    <MenuItem key={metodo} value={metodo}>{metodo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item size={6}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Referencia
              </Typography>
              <TextField
                fullWidth
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                placeholder="Número de factura o referencia"
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Descripción
              </Typography>
              <TextField
                fullWidth
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primarybutton"
            disabled={!formData.concepto || !formData.monto || !formData.metodoPago}
          >
            {editMode ? 'Actualizar' : 'Registrar'} Gasto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        handleClose={handleCloseAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        severity={alertConfig.severity}
      />
    </AppTheme>
  );
};

export default GastosManagement;

